import React, {FC, useCallback} from 'react';

import {connect} from 'react-redux';

import {ApplicationStoreType} from 'desktop/store';
import {CardType} from 'desktop/types/creditCardType';

import {PAYMENTS} from '../../constants/paymentStep';
import {setOptionPaymentMethod} from '../../store/reducers/analytics/analytics';
import {save} from '../../store/reducers/paymentStep/creditCard/creditCard';
import {setSelectedMethod} from '../../store/reducers/paymentStep/selectedMethod/selectedMethod';
import {getCustomerName} from '../../store/selectors/billingAddress/billingAddress';
import {
    getErrorMessage,
    getIsLoading,
    getSavedCards,
    getShowSavedCards,
} from '../../store/selectors/creditCard/creditCard';
import {getNameSelectedMethod} from '../../store/selectors/paymentStep/paymentStep';
import {
    errorHandler,
    saveCardChangeHandler,
    sendAnalyticsEventOnInvalid,
    sendAnalyticsOnPlaceOrder,
} from '../paymentCreditCardWrapper/analyticsEvents';
import {PaymentFooter} from '../paymentFooter/paymentFooter';
import {PaymentOsCardForm} from '../paymentOsCardFormV3/paymentOsCardFormV3';
import {SavedCards} from '../savedCards/savedCards';
import {SavedCardsSwitcherConnected} from '../savedCardsSwitcher/savedCardsSwitcher';

export type PaymentCreditCardType = {
    isLoading: boolean;
    showSavedCards: boolean;
    savePayment: (data: {token: string; shouldSave: boolean; cvv?: string}) => void;
    cards: CardType[];
    setOptionPayment: () => void;
    error: string;
    customerName?: string;
    selectMethod: (method: PAYMENTS) => void;
    selectedMethodName: string;
};

/**
 * Payment credit card wrapper for checkout
 */
export const PaymentCreditCard: FC<PaymentCreditCardType> = ({
    cards,
    customerName,
    error,
    isLoading,
    savePayment,
    selectedMethodName,
    selectMethod,
    setOptionPayment,
    showSavedCards,
}) => {
    const submitHandler = useCallback(
        (data: {token: string; cvv: string}, {shouldSave}: {shouldSave: boolean}) => {
            savePayment({cvv: data.cvv, shouldSave, token: data.token});
        },
        [selectedMethodName, selectMethod, savePayment],
    );

    return (
        <>
            {showSavedCards ? (
                <SavedCards
                    cards={cards}
                    isLoading={isLoading}
                    onSavePayment={() => setOptionPayment()}
                    savePayment={savePayment}
                />
            ) : (
                <PaymentOsCardForm
                    button={(inProgress, onSubmit) => (
                        <PaymentFooter
                            onSubmit={() => {
                                sendAnalyticsOnPlaceOrder(error);
                                setOptionPayment();
                                onSubmit();
                            }}
                            isLoading={inProgress || isLoading}
                        />
                    )}
                    cardholderName={customerName}
                    onError={errorHandler}
                    onInvalid={sendAnalyticsEventOnInvalid}
                    onSaveCardChange={saveCardChangeHandler}
                    onSubmit={submitHandler}
                    isSideReview
                    withSave
                >
                    {Boolean(cards.length) && <SavedCardsSwitcherConnected />}
                </PaymentOsCardForm>
            )}
        </>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux stores
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    cards: getSavedCards(store),
    customerName: getCustomerName(store),
    error: getErrorMessage(store),
    isLoading: getIsLoading(store),
    selectedMethodName: getNameSelectedMethod(store),
    showSavedCards: getShowSavedCards(store),
});

export const PaymentCreditCardConnected = connect(mapStateToProps, {
    savePayment: save,
    selectMethod: setSelectedMethod,
    setOptionPayment: setOptionPaymentMethod,
})(PaymentCreditCard);
